import { observer } from 'mobx-react-lite';
import React from 'react';
import { Controller } from 'react-hook-form';

import { LegacyGrid as Grid, LegacyGridCol as GridCol } from '@flixbus/honeycomb-react';

import CrewMemberTextSearch from 'common/CrewMemberTextSearch';
import DaterangeFilter from 'common/DaterangeFilter';
import LocationMultiSearch from 'common/LocationMultiSearch';

import AvailabilityStatusFilter from './AvailabilityStatusFilter';
import DisplaySelector from './DisplaySelector';
import GroupSearch from './Group';
import OperatorTypeFilter from './OperatorTypeFilter';
import PageSizeSelector from './PageSizeSelector';
import TimezoneTLSelect from './TimezoneTLSelect';
import { useTimelineFilters } from './useTimelineFilters';

import styles from './styles.module.css';

const TimelineFilters = observer(function TimelineFilters({ onFiltersSubmitted }) {
	const {
		control,
		dateKey,
		dirtyFields,
		selectedTimezone,
		setValue,
		formSubmitHandler,
		submitOnChange,
		handleReset,
		handleResetSingleField,
		handleTimezoneChange,
	} = useTimelineFilters({ onFiltersSubmitted });

	return (
		<form autoComplete="off" className={styles.filters} onSubmit={formSubmitHandler}>
			{!!Object.keys(dirtyFields).length && (
				<div className={styles.reset} onClick={handleReset}>
					Reset Filters
				</div>
			)}
			<div className={styles.timeLineGridWrapper}>
				<div className={styles.filtersLeftColumn}>
					<div className={styles.crewMemberSearchWrapper}>
						<Controller
							name="text"
							control={control}
							render={({ field, fieldState }) => (
								<CrewMemberTextSearch
									id="search"
									label="Search"
									placeholder="Search"
									fieldControl={field}
									isDirty={fieldState.isDirty}
									submitOnChange={submitOnChange}
									onReset={() => handleResetSingleField('text')}
								/>
							)}
						/>
					</div>
				</div>

				<div className={styles.filtersGridAligned}>
					<Grid justify="left">
						<GridCol size={3}>
							<Controller
								key={`dateRange-${dateKey}`}
								name="dateRange"
								control={control}
								render={({ field }) => (
									<DaterangeFilter
										id="timeline-from-date"
										label="Show from date"
										onReset={() => handleResetSingleField('dateRange')}
										fieldControl={field}
										onSubmitOnChange={submitOnChange}
										doSubmitOnChange
									/>
								)}
							/>
						</GridCol>

						<GridCol size={4}>
							<Controller
								name="locationAvailabilityCity"
								control={control}
								render={({ field }) => (
									<LocationMultiSearch
										label="Locations"
										fieldControl={field}
										setValue={setValue}
										submitOnChange={submitOnChange}
									/>
								)}
							/>
						</GridCol>

						<GridCol size={4}>
							<TimezoneTLSelect selected={selectedTimezone} onSelection={handleTimezoneChange} />
						</GridCol>
					</Grid>

					<Grid justify="right">
						<GridCol>
							<Controller
								name="operatorType"
								control={control}
								render={({ field }) => (
									<OperatorTypeFilter fieldControl={field} submitOnChange={submitOnChange} />
								)}
							/>
						</GridCol>

						<GridCol>
							<Controller
								name="status"
								control={control}
								render={({ field }) => (
									<AvailabilityStatusFilter fieldControl={field} submitOnChange={submitOnChange} />
								)}
							/>
						</GridCol>

						<GridCol>
							<Controller
								name="display"
								control={control}
								render={({ field }) => (
									<DisplaySelector fieldControl={field} submitOnChange={submitOnChange} />
								)}
							/>
						</GridCol>

						<GridCol>
							<Controller
								name="group"
								control={control}
								render={({ field }) => (
									<GroupSearch fieldControl={field} submitOnChange={submitOnChange} />
								)}
							/>
						</GridCol>

						<GridCol>
							<Controller
								name="pageSize"
								control={control}
								render={({ field }) => (
									<PageSizeSelector fieldControl={field} submitOnChange={submitOnChange} />
								)}
							/>
						</GridCol>
					</Grid>
				</div>
			</div>
		</form>
	);
});

export default TimelineFilters;
